'use client';

import React, { useMemo, useState } from 'react';
import { useBreakpoint } from '../../hooks';
import { CatHeading } from '@blocks-react/components/Heading';
import { CatIconTrendingUp } from '@blocks-react/components/Icons/TrendingUp';
import { CatButton } from '@blocks-react/components/Button';
import { CatIconChevronDown } from '@blocks-react/components/Icons/ChevronDown';
import { CatIconChevronUp } from '@blocks-react/components/Icons/ChevronUp';
import cx from 'classnames';
import { PAGE_BREAKPOINTS } from '../../constants/commonConstants';
import { CARD_PER_PAGE } from './constants';
import { instanceProps, ServiceTranslationProps } from './types';
import { Carousel, Conditional } from '../common';
import { ServiceRecommendationList } from './service-recommendation-list';
import { ServiceRecommendationError } from './service-recommendation-error';
import styles from './serviceRecommendation.module.scss';

type ServiceRecommendationProps = {
  instanceData: instanceProps[];
  serviceTranslation: ServiceTranslationProps;
  handleModal: () => void;
  errorStatus: boolean;
};

export const ServiceRecommendation = ({
  instanceData = [],
  serviceTranslation,
  handleModal,
  errorStatus = false
}: ServiceRecommendationProps) => {
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const [viewMore, setViewMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const handleClick = () => {
    setViewMore((Prev) => !Prev);
  };

  const instanceDataList = useMemo(() => {
    return instanceData.slice(currentPage, currentPage + CARD_PER_PAGE);
  }, [currentPage, instanceData]);

  return (
    <div className={styles['service_recommendation_section']}>
      <div className={styles['service_recommendation_title']}>
        <CatHeading
          variant="title"
          tabIndex={0}
          aria-label={serviceTranslation.title}
        >
          {serviceTranslation.title}
        </CatHeading>
        <CatIconTrendingUp
          className={styles['service_recommendation_icon']}
          size="sm"
        />
      </div>
      {/* card display section */}
      <Conditional
        test={!errorStatus}
        fallback={
          <ServiceRecommendationError
            errorHeader={serviceTranslation.errorHeader}
            errorContent={serviceTranslation.errorContent}
          />
        }
      >
        <div
          className={cx(`${styles['service_recommendation_card']} `, {
            [styles['service_recommendation_card_mobile']]: !isMD,
            [styles['service_recommendation_card_tablet']]: !isLG
          })}
        >
          <Conditional
            test={!!isLG || !!isMD}
            fallback={
              <ServiceRecommendationList
                handleModal={handleModal}
                serviceTranslation={serviceTranslation}
                instanceData={
                  !isMD && !viewMore ? instanceData.slice(0, 2) : instanceData
                }
              />
            }
          >
            <Carousel
              cardPerPage={CARD_PER_PAGE}
              currentPage={currentPage}
              totalCard={instanceData.length}
              desktopView={!!isLG}
              setcurrentPage={setCurrentPage}
              buttonTranslation={serviceTranslation?.carouselButton}
            >
              <ServiceRecommendationList
                handleModal={handleModal}
                serviceTranslation={serviceTranslation}
                instanceData={instanceDataList}
              />
            </Carousel>
          </Conditional>
          {!isMD && instanceData.length > 2 && (
            <CatButton
              onClick={handleClick}
              variant="ghost"
              size="sm"
              className={styles['service_recommendation_card_mobile_button']}
            >
              {!viewMore ? (
                <React.Fragment key=".0">
                  {serviceTranslation.viewAll}
                  <CatIconChevronDown slot="after" />
                </React.Fragment>
              ) : (
                <React.Fragment key=".0">
                  {serviceTranslation.viewLess}
                  <CatIconChevronUp slot="after" />
                </React.Fragment>
              )}
            </CatButton>
          )}
        </div>
      </Conditional>
    </div>
  );
};
